import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Eye, EyeOff, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtError } from "@/lib/api";
import { AuthShell, AuthKicker, AuthTitle, CARD } from "@/components/landing/AuthShell";


export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Connecté");
      nav(u.role === "admin" ? "/app/admin" : "/app/dashboard");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell promise="Connexion">
      <Helmet>
        <title>Connexion — VosCompagnons</title>
        <meta name="description" content="Connectez-vous à votre espace VosCompagnons — plateforme B2B pour prestataires du bâtiment et donneurs d'ordre." />
        <link rel="canonical" href="https://voscompagnons.fr/connexion" />
        <meta property="og:title" content="Connexion — VosCompagnons" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voscompagnons.fr/connexion" />
        <meta property="og:locale" content="fr_FR" />
      </Helmet>
      <AuthKicker>Connexion</AuthKicker>
      <AuthTitle>Content de vous revoir.</AuthTitle>
      <div className="mt-6 md:mt-8">
          <form
            onSubmit={submit}
            className={CARD}
            data-testid="login-form"
          >
            <div>
              <label htmlFor="login-email" className="label">E-mail</label>
              <input
                id="login-email"
                required
                type="email"
                autoComplete="email"
                inputMode="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label htmlFor="login-password" className="label">Mot de passe</label>
                <Link
                  to="/mot-de-passe-oublie"
                  className="text-xs text-[#3E4C59] hover:text-[#E8710A] transition-colors"
                  data-testid="login-forgot-link"
                >
                  Oublié ?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  required
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  className="input pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-[#3E4C59] hover:text-[#1B3A5C]"
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  data-testid="login-password-toggle"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              disabled={busy}
              className="btn-primary w-full min-h-[56px] text-base"
              data-testid="login-submit"
            >
              {busy ? "Connexion…" : "Se connecter"}
              {!busy && <ArrowUpRight size={18} />}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#3E4C59] text-center">
            Pas encore de compte ?{" "}
            <Link
              to="/inscription"
              className="font-bold text-[#1B3A5C] underline hover:text-[#E8710A] transition-colors"
              data-testid="login-register-link"
            >
              Créer mon compte
            </Link>
          </p>
      </div>
    </AuthShell>
  );
}
